import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import BackgroundEffects from '../../../components/layout/BackgroundEffects/BackgroundEffects'
import Header from '../../../components/layout/Header/Header'
import Card from '../../../components/common/Card/Card'
import Button from '../../../components/common/Button/Button'
import { getCurrentUser, logout, getUserData } from '../../../services/authService'
import {
  getHolidaysByYear,
  createHoliday,
  updateHoliday,
  deleteHoliday,
  groupHolidaysByMonth,
  isUpcoming,
  getAvailableYears
} from '../../../services/holidaysService'
import HolidayCard from '../components/HolidayCard'
import HolidayModal from '../components/HolidayModal'

const Holidays = () => {
  const navigate = useNavigate()

  // Auth & User State
  const [user, setUser] = useState(null)
  const [userData, setUserData] = useState(null)
  const [isAdmin, setIsAdmin] = useState(false)

  // Holidays State
  const [holidays, setHolidays] = useState([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingHoliday, setEditingHoliday] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')

  const availableYears = getAvailableYears()

  // Load user data and holidays
  useEffect(() => {
    loadUserAndHolidays()
  }, [selectedYear])

  const loadUserAndHolidays = async () => {
    try {
      setLoading(true)
      setError('')

      const currentUser = getCurrentUser()
      if (!currentUser) {
        navigate('/login')
        return
      }

      setUser(currentUser)

      // Get user data from Firestore
      const userDataResult = await getUserData(currentUser.uid)

      if (!userDataResult.success) {
        setError('Failed to load user data')
        setLoading(false)
        return
      }

      setUserData(userDataResult.data)

      // Check if user is admin
      const adminStatus =
        userDataResult.data.role === 'super_admin' ||
        userDataResult.data.role === 'admin'
      setIsAdmin(adminStatus)

      // Load holidays for selected year
      const result = await getHolidaysByYear(selectedYear)
      if (result.success) {
        setHolidays(result.data)
      } else {
        setError(result.error || 'Failed to load holidays')
      }
    } catch (error) {
      console.error('Load error:', error)
      setError('An error occurred while loading data')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = async () => {
    const result = await logout()
    if (result.success) {
      navigate('/login')
    }
  }

  const handleBack = () => {
    navigate('/dashboard')
  }

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value, 10))
  }

  const handleOpenModal = () => {
    setEditingHoliday(null)
    setIsModalOpen(true)
  }

  const handleEditHoliday = (holiday) => {
    setEditingHoliday(holiday)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setEditingHoliday(null)
  }

  const handleSubmitHoliday = async (formData) => {
    setIsSubmitting(true)
    setError('')
    setSuccessMessage('')

    try {
      if (editingHoliday) {
        // Update existing holiday
        const result = await updateHoliday(editingHoliday.id, formData)

        if (result.success) {
          // Reload holidays to get updated data
          await loadUserAndHolidays()
          setSuccessMessage('Holiday updated successfully!')
          handleCloseModal()
        } else {
          setError(result.error || 'Failed to update holiday')
        }
      } else {
        // Create new holiday
        const adminName = userData?.fullName || user?.email || 'Admin'
        const result = await createHoliday(formData, adminName)

        if (result.success) {
          // Reload holidays to include the new one
          await loadUserAndHolidays()
          setSuccessMessage('Holiday added successfully!')
          handleCloseModal()
        } else {
          setError(result.error || 'Failed to create holiday')
        }
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000)
    } catch (error) {
      console.error('Submit error:', error)
      setError('An error occurred while saving the holiday')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteHoliday = async (id) => {
    try {
      const result = await deleteHoliday(id)

      if (result.success) {
        // Remove from local state
        setHolidays(prev => prev.filter(h => h.id !== id))
        setSuccessMessage('Holiday deleted successfully!')
        setTimeout(() => setSuccessMessage(''), 3000)
      } else {
        setError(result.error || 'Failed to delete holiday')
        setTimeout(() => setError(''), 5000)
      }
    } catch (error) {
      console.error('Delete error:', error)
      setError('An error occurred while deleting the holiday')
      setTimeout(() => setError(''), 5000)
    }
  }

  // Group holidays by month
  const groupedHolidays = groupHolidaysByMonth(holidays)

  // Loading State
  if (loading) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Holidays"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-spinner fa-spin"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-primary)',
              marginBottom: '1rem'
            }}
          ></i>
          <p style={{ color: 'var(--psyber-text-secondary)' }}>Loading holidays...</p>
        </Card>
      </div>
    )
  }

  // Error State (Critical Error)
  if (error && holidays.length === 0 && !successMessage) {
    return (
      <div className="psyber-container psyber-portal-container">
        <BackgroundEffects />
        <Header
          user={userData || user}
          onLogout={handleLogout}
          showBackButton={true}
          onBack={handleBack}
          title="Holidays"
        />
        <Card style={{ textAlign: 'center', padding: '3rem' }}>
          <i
            className="fas fa-exclamation-triangle"
            style={{
              fontSize: '3rem',
              color: 'var(--psyber-error)',
              marginBottom: '1rem'
            }}
          ></i>
          <h3 style={{ marginBottom: '1rem', color: 'var(--psyber-error)' }}>Error</h3>
          <p style={{ color: 'var(--psyber-text-secondary)', marginBottom: '2rem' }}>{error}</p>
          <Button variant="primary" icon="fas fa-redo" onClick={loadUserAndHolidays}>
            Retry
          </Button>
        </Card>
      </div>
    )
  }

  // Empty State
  const EmptyState = () => (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <i
        className="fas fa-calendar-xmark"
        style={{
          fontSize: '4rem',
          color: 'var(--psyber-text-muted)',
          marginBottom: '1.5rem',
          display: 'block'
        }}
      ></i>
      <h3 style={{ marginBottom: '0.5rem', color: 'var(--psyber-text)' }}>
        No Holidays Found
      </h3>
      <p style={{ color: 'var(--psyber-text-secondary)' }}>
        {isAdmin
          ? 'Click "Add Holiday" to create the first holiday for this year'
          : `No holidays have been added for ${selectedYear} yet`}
      </p>
    </div>
  )

  return (
    <div className="psyber-container psyber-portal-container">
      <BackgroundEffects />

      <Header
        user={userData || user}
        onLogout={handleLogout}
        showBackButton={true}
        onBack={handleBack}
        title="Holidays"
      />

      {/* Success Message */}
      {successMessage && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-success-bg)',
            border: '2px solid var(--psyber-success)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-check-circle" style={{ color: 'var(--psyber-success)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-success)', fontWeight: 600 }}>{successMessage}</p>
          </div>
        </Card>
      )}

      {/* Error Message */}
      {error && holidays.length > 0 && (
        <Card
          style={{
            marginBottom: '1.5rem',
            background: 'var(--psyber-error-bg)',
            border: '2px solid var(--psyber-error)'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <i className="fas fa-exclamation-circle" style={{ color: 'var(--psyber-error)', fontSize: '1.5rem' }}></i>
            <p style={{ margin: 0, color: 'var(--psyber-error)', fontWeight: 600 }}>{error}</p>
          </div>
        </Card>
      )}

      {/* Header Card with Year Filter */}
      <Card style={{ marginBottom: '2rem' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <div style={{ flex: 1 }}>
            <h2 style={{
              margin: 0,
              fontSize: 'var(--psyber-font-size-2xl)',
              color: 'var(--psyber-text)',
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem'
            }}>
              <i className="fas fa-calendar-star"></i>
              Company Holidays
            </h2>
            <p style={{
              margin: '0.5rem 0 0 0',
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)'
            }}>
              {holidays.length === 0
                ? `No holidays for ${selectedYear}`
                : `${holidays.length} holiday${holidays.length !== 1 ? 's' : ''} in ${selectedYear}`}
            </p>
          </div>

          {/* Year Filter and Actions */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
            <div>
              <label
                htmlFor="yearFilter"
                style={{
                  display: 'block',
                  marginBottom: '0.25rem',
                  fontSize: 'var(--psyber-font-size-xs)',
                  color: 'var(--psyber-text-secondary)',
                  fontWeight: 600
                }}
              >
                Year
              </label>
              <select
                id="yearFilter"
                value={selectedYear}
                onChange={handleYearChange}
                className="psyber-input"
                style={{
                  cursor: 'pointer',
                  minWidth: '120px'
                }}
              >
                {availableYears.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {isAdmin && (
              <div style={{ alignSelf: 'flex-end' }}>
                <Button
                  variant="primary"
                  icon="fas fa-plus"
                  onClick={handleOpenModal}
                >
                  Add Holiday
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Holidays Display */}
      <Card>
        {holidays.length === 0 ? (
          <EmptyState />
        ) : (
          <div>
            {Object.entries(groupedHolidays).map(([month, monthHolidays]) => (
              <div
                key={month}
                style={{
                  marginBottom: '2rem',
                  paddingBottom: '2rem',
                  borderBottom: '1px solid var(--psyber-card-border)'
                }}
              >
                {/* Month Heading */}
                <h3 style={{
                  marginBottom: '1rem',
                  fontSize: 'var(--psyber-font-size-xl)',
                  color: 'var(--psyber-text)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <i className="fas fa-calendar-days"></i>
                  {month}
                </h3>

                {/* Month Holidays Grid */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '1rem'
                }}>
                  {monthHolidays.map((holiday) => (
                    <HolidayCard
                      key={holiday.id}
                      holiday={holiday}
                      isUpcoming={isUpcoming(holiday.date, holiday.endDate)}
                      isAdmin={isAdmin}
                      onEdit={handleEditHoliday}
                      onDelete={handleDeleteHoliday}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Help Section for Employees */}
      {!isAdmin && holidays.length > 0 && (
        <Card style={{ marginTop: '2rem' }}>
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <i className="fas fa-info-circle"></i> About Holidays
          </h3>
          <div
            style={{
              fontSize: 'var(--psyber-font-size-sm)',
              color: 'var(--psyber-text-secondary)',
              lineHeight: 1.6
            }}
          >
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Upcoming Holidays:</strong> Holidays highlighted in blue are upcoming or happening today.
            </p>
            <p style={{ marginBottom: '0.75rem' }}>
              <strong>Holiday Types:</strong> National (public holidays), Company (organization-specific), Optional (flexible holidays).
            </p>
            <p style={{ margin: 0 }}>
              <strong>Planning Leave?</strong> Visit the Time Off module to submit leave requests around holidays.
            </p>
          </div>
        </Card>
      )}

      {/* Holiday Modal */}
      <HolidayModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleSubmitHoliday}
        holiday={editingHoliday}
        isLoading={isSubmitting}
      />
    </div>
  )
}

export default Holidays
